import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { LifeBuoy, Send, ArrowLeft, Filter } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["all", "open", "in_progress", "resolved", "closed"];
const CATEGORIES = ["all", "technical", "billing", "content", "domain", "other"];

function statusChip(s) {
    if (s === "open") return "ddi-chip ddi-chip-primary";
    if (s === "in_progress") return "ddi-chip ddi-chip-warning";
    if (s === "resolved") return "ddi-chip ddi-chip-success";
    return "ddi-chip ddi-chip-neutral";
}

export default function AdminSupport() {
    const [tickets, setTickets] = useState([]);
    const [counts, setCounts] = useState(null);
    const [selected, setSelected] = useState(null);
    const [thread, setThread] = useState(null);
    const [reply, setReply] = useState("");
    const [newStatus, setNewStatus] = useState("");
    const [fStatus, setFStatus] = useState("all");
    const [fCat, setFCat] = useState("all");

    async function load() {
        try {
            const q = new URLSearchParams();
            if (fStatus !== "all") q.set("status", fStatus);
            if (fCat !== "all") q.set("category", fCat);
            const [r, c] = await Promise.all([
                api.get(`/admin/tickets?${q.toString()}`),
                api.get("/admin/tickets/counts"),
            ]);
            setTickets(r.data || []);
            setCounts(c.data);
        } catch (e) { toast.error(errMsg(e, "Failed")); }
    }
    useEffect(() => { load(); }, [fStatus, fCat]);

    async function open(id) {
        setSelected(id);
        setReply("");
        setNewStatus("");
        try {
            const r = await api.get(`/admin/tickets/${id}`);
            setThread(r.data);
            setNewStatus(r.data.ticket.status);
        } catch (e) { toast.error(errMsg(e, "Failed")); }
    }

    async function sendReply() {
        if (!reply.trim() || !selected) return;
        try {
            await api.post(`/admin/tickets/${selected}/reply`, { message: reply, status: newStatus });
            setReply("");
            await open(selected);
            await load();
            toast.success("Reply sent");
        } catch (e) { toast.error(errMsg(e, "Failed")); }
    }

    async function changeStatus(s) {
        try {
            await api.post(`/admin/tickets/${selected}/status`, { status: s });
            setNewStatus(s);
            await load();
            toast.success(`Status → ${s}`);
        } catch (e) { toast.error(errMsg(e, "Failed")); }
    }

    return (
        <AdminShell>
            <div className="mb-6 flex items-start justify-between flex-wrap gap-3" data-testid="admin-support-header">
                <div>
                    <h1 className="ddi-section-title">Support tickets</h1>
                    <p className="ddi-section-sub">Reply to doctor tickets and update their statuses.</p>
                </div>
                {counts && (
                    <div className="flex gap-2 flex-wrap">
                        <span className="ddi-chip ddi-chip-primary">Open · {counts.open}</span>
                        <span className="ddi-chip ddi-chip-warning">In progress · {counts.in_progress}</span>
                        <span className="ddi-chip ddi-chip-success">Resolved · {counts.resolved}</span>
                        {counts.unread_by_admin > 0 && <span className="ddi-chip ddi-chip-error">Unread · {counts.unread_by_admin}</span>}
                    </div>
                )}
            </div>

            {/* Filters */}
            <Card className="border-[#E5E9F2] mb-5 ddi-shadow-sm">
                <CardContent className="py-4 flex items-center gap-3 flex-wrap">
                    <Filter size={14} className="text-[#64748B]" />
                    <div className="flex items-center gap-2">
                        <span className="text-[12px] text-[#64748B]">Status</span>
                        <Select value={fStatus} onValueChange={setFStatus}>
                            <SelectTrigger className="w-40" data-testid="admin-filter-status"><SelectValue /></SelectTrigger>
                            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-[12px] text-[#64748B]">Category</span>
                        <Select value={fCat} onValueChange={setFCat}>
                            <SelectTrigger className="w-40" data-testid="admin-filter-category"><SelectValue /></SelectTrigger>
                            <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <Card className="lg:col-span-5 border-[#E5E9F2] ddi-shadow-sm">
                    <CardHeader className="pb-3"><CardTitle className="text-base">Tickets <span className="text-[#94A3B8] font-normal text-[13px]">· {tickets.length}</span></CardTitle></CardHeader>
                    <CardContent className="p-0">
                        {tickets.length === 0 ? (
                            <div className="p-6 text-center text-[13px] text-[#64748B]">
                                <LifeBuoy size={32} className="mx-auto text-[#94A3B8] mb-2" />
                                No tickets match.
                            </div>
                        ) : (
                            <ul className="divide-y divide-[#EEF1F6] max-h-[600px] overflow-auto">
                                {tickets.map((t) => (
                                    <li key={t.id}>
                                        <button onClick={() => open(t.id)} className={"w-full text-left px-4 py-3 hover:bg-[#F5F7FB] " + (selected === t.id ? "bg-[#EEF4FE] border-l-4 border-[#3A80F7]" : "")} data-testid={`admin-ticket-${t.id}`}>
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className={statusChip(t.status)}>{t.status.replace("_", " ")}</span>
                                                <span className="ddi-chip ddi-chip-neutral">{t.category}</span>
                                                {t.unread_by_admin && <span className="w-2 h-2 rounded-full bg-[#DC2626]" title="Unread" />}
                                            </div>
                                            <div className="text-[14px] font-semibold text-[#0F172A] truncate">{t.subject}</div>
                                            <div className="text-[11px] text-[#64748B] mt-0.5">
                                                Dr. {t.doctor?.full_name} · {t.doctor?.specialization} · {t.reply_count} replies · Updated {new Date(t.last_activity_at).toLocaleString()}
                                            </div>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </CardContent>
                </Card>

                <div className="lg:col-span-7">
                    {thread ? (
                        <Card className="border-[#E5E9F2] ddi-shadow-sm">
                            <CardHeader className="pb-3">
                                <button onClick={() => { setSelected(null); setThread(null); }} className="text-[#64748B] hover:text-[#0F172A] text-[12px] flex items-center gap-1 mb-2 lg:hidden"><ArrowLeft size={12} /> Back</button>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className={statusChip(thread.ticket.status)}>{thread.ticket.status.replace("_", " ")}</span>
                                    <span className="ddi-chip ddi-chip-neutral">{thread.ticket.category}</span>
                                </div>
                                <CardTitle className="text-base">{thread.ticket.subject}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2" data-testid="admin-thread">
                                    {(thread.messages || []).map((m) => {
                                        const isAdmin = m.author_role !== "doctor";
                                        return (
                                            <div key={m.id} className={"rounded-xl p-3 border " + (isAdmin ? "bg-[#EEF4FE] border-[#3A80F7]/20 ml-6" : "bg-white border-[#E5E9F2] mr-6")}>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className={"text-[11px] font-semibold " + (isAdmin ? "text-[#3A80F7]" : "text-[#64748B]")}>
                                                        {isAdmin ? `Support · ${m.author_email || ""}` : "Doctor"}
                                                    </span>
                                                    <span className="text-[10px] text-[#94A3B8]">{new Date(m.created_at).toLocaleString()}</span>
                                                </div>
                                                <div className="text-[13px] text-[#0F172A] whitespace-pre-line">{m.message}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="mt-4 pt-4 border-t border-[#E5E9F2] space-y-3">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[11px] text-[#64748B]">Update status:</span>
                                        {["open", "in_progress", "resolved", "closed"].map((s) => (
                                            <button
                                                key={s}
                                                onClick={() => changeStatus(s)}
                                                className={"text-[11px] px-2.5 py-1 rounded-md " + (thread.ticket.status === s ? "bg-[#3A80F7] text-white" : "bg-[#F5F7FB] text-[#334155] hover:bg-[#EEF4FE]")}
                                                data-testid={`admin-set-status-${s}`}
                                            >
                                                {s.replace("_", " ")}
                                            </button>
                                        ))}
                                    </div>
                                    <Textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type your reply…" maxLength={5000} data-testid="admin-reply-input" />
                                    <div className="flex items-center gap-2 justify-end">
                                        <Select value={newStatus} onValueChange={setNewStatus}>
                                            <SelectTrigger className="w-40" data-testid="admin-reply-status"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {["open", "in_progress", "resolved", "closed"].map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                        <Button onClick={sendReply} disabled={!reply.trim() || thread.ticket.status === "closed"} className="bg-[#3A80F7] hover:bg-[#2D6BE0]" data-testid="admin-reply-send">
                                            <Send size={14} className="mr-2" /> Send + set status
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ) : (
                        <Card className="border-[#E5E9F2] border-dashed">
                            <CardContent className="py-16 text-center">
                                <LifeBuoy size={40} className="mx-auto text-[#94A3B8] mb-3" />
                                <div className="text-[15px] font-semibold text-[#0F172A]">Select a ticket</div>
                                <div className="text-[13px] text-[#64748B] mt-1">Choose a ticket to view its thread and reply.</div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>
        </AdminShell>
    );
}
